import { AxiosInstance } from "axios";

export interface UpdateProposalCommissionPayload {
  returnedType: string;
  returnedAmount?: number | null;
  returnedHeapedAmount?: number[] | null;
}

export interface UpdateProposalFreeTextPayload {
  compsTechSubsidyText: string | null;
  compsImplementationCreditText: string | null;
  compsWellnessDollarsText: string | null;
  compsNotesText: string | null;
}

/** Identifies the Requested or Incumbent column in the Comps grid. */
export type CompsColumnKind = "requested" | "incumbent";

/**
 * Payload for materializing an analyst proposal plan and its commission in one
 * atomic call (ADR-024). Used when an analyst edits a carrier commission cell in
 * the Comps grid that has no carrier-sourced proposal plan yet.
 */
export interface MaterializeCommissionPayload {
  carrierId: string;
  /** Display name for carrierId; omitted (or equal to the slug) falls back to carrierId. */
  carrierName?: string;
  rfpBenefitPlanGroupId: string;
  planPosition: number;
  commission: UpdateProposalCommissionPayload;
}

const requireAxios = (axiosInstance?: AxiosInstance): AxiosInstance => {
  if (!axiosInstance) {
    throw new Error("Axios instance required for Comps API");
  }
  return axiosInstance;
};

export const updateProposalCommission = async (
  rfpId: string,
  proposalId: string,
  proposalPlanId: string,
  payload: UpdateProposalCommissionPayload,
  axiosInstance?: AxiosInstance,
): Promise<void> => {
  const axios = requireAxios(axiosInstance);

  await axios.patch<void>(
    `/btx/v1/rfps/${encodeURIComponent(rfpId)}/proposals/${encodeURIComponent(
      proposalId,
    )}/commissions/${encodeURIComponent(proposalPlanId)}`,
    payload,
  );
};

export const updateProposalFreeText = async (
  rfpId: string,
  proposalId: string,
  payload: UpdateProposalFreeTextPayload,
  axiosInstance?: AxiosInstance,
): Promise<void> => {
  const axios = requireAxios(axiosInstance);

  await axios.patch<void>(
    `/btx/v1/rfps/${encodeURIComponent(rfpId)}/proposals/${encodeURIComponent(
      proposalId,
    )}/free-text`,
    payload,
  );
};

/**
 * Updates the free-text fields for the Requested or Incumbent column. Unlike
 * carrier columns, these aren't backed by a proposal, so they're keyed by
 * (rfpId, columnKind) instead of a proposalId.
 */
export const updateCompsColumnFreeText = async (
  rfpId: string,
  columnKind: CompsColumnKind,
  payload: UpdateProposalFreeTextPayload,
  axiosInstance?: AxiosInstance,
): Promise<void> => {
  const axios = requireAxios(axiosInstance);

  await axios.patch<void>(
    `/btx/v1/rfps/${encodeURIComponent(rfpId)}/comps/column-text/${encodeURIComponent(
      columnKind,
    )}`,
    payload,
  );
};

/**
 * Materializes an analyst-sourced proposal plan carrying the analyst's commission
 * for a carrier/coverage that has no carrier-sourced data yet (ADR-024), in one
 * atomic call. Mirrors the SBS grid's materialize-on-edit flow.
 */
export const materializeProposalCommission = async (
  rfpId: string,
  payload: MaterializeCommissionPayload,
  axiosInstance?: AxiosInstance,
): Promise<void> => {
  const axios = requireAxios(axiosInstance);
  const {
    carrierId,
    carrierName,
    rfpBenefitPlanGroupId,
    planPosition,
    commission,
  } = payload;

  const hasResolvedName = !!carrierName && carrierName !== carrierId;

  await axios.post<void>(
    `/btx/v1/rfps/${encodeURIComponent(rfpId)}/proposals`,
    {
      carrierId,
      ...(hasResolvedName ? { carrierName } : {}),
      origin: "analyst",
      materialize: {
        rfpBenefitPlanGroupId,
        planPosition,
        commission,
      },
    },
  );
};
